import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../../config";
import logoImage from "../../../assets/eventrender.png";
import { Eye, EyeOff } from "lucide-react";
import { purewebContext } from "../../../../context/PurewebContext";
import { msalInstance } from "../../../../msalConfig";
import { AiOutlineCheckCircle } from "react-icons/ai";
import ClaritySeeThroughBackdrop from "./ClaritySeeThroughBackdrop";


const IS_PRODUCTION = process.env.REACT_APP_ENVIRONMENT === "production";

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const LoginPage = ({
  sendMessage,
  error,
  setError,
  loginLoading,
  setLoginLoading,
  success,
  setSuccess,
  resetPasswordLoading,
  setResetPasswordLoading,
  newAccountLoading,
  setNewAccountLoading,
  initialEmail = "",
  initialMode = "login",
  onAccountCreatedContinue,
  // Direct-login mode (/render?action=login, every submit): credentials are
  // validated against the backend first — never sent straight to UE, whose login
  // state may not be ready even after RenderNow_Loaded — "Create New Account"
  // routes into the signup intake flow, and Reset Password (a UE-only flow the
  // website Login button must not expose) is hidden for the whole session.
  onDirectLogin,
  // Direct-login counterpart for the Google/Microsoft buttons: we run the OAuth popup
  // and hand over the raw token, then the parent validates it, holds it until UE is
  // ready, and owns error/loading state from there — exactly as onDirectLogin does for
  // a password. Without it these paths would send a token to UE the moment the popup
  // closed, long before the stream exists.
  onDirectSocialLogin,
  onGoogleTrigger,
  // Called when a 30s safety net below fires, so the parent can check whether the
  // stream itself died (and say so) rather than leave only "please try again".
  onLoginTimeout,
  onSignupRedirect,
  hideResetPassword = false,
  // Linking a Google login to an existing password account: UE found the email, so the
  // form is locked to it and asks for the password once. The parent's onGoogleLinkLogin
  // checks it, opens the Google tab inside this click, and runs the login.
  linkEmail = "",
  onGoogleLinkLogin,
  onLinkCancel,
  // Show the game behind this popup in Clarity replays (see ClaritySeeThroughBackdrop).
  claritySeeThrough = false,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [resetPasswordMode, setResetPasswordMode] = useState(false);
  const [newAccountMode, setNewAccountMode] = useState(initialMode === "newAccount");
  const [loginMode, setLoginMode] = useState(initialMode !== "newAccount");
  const [showPassword, setShowPassword] = useState(false);
  const [loginRetryInterval, setLoginRetryInterval] = useState(null);
  const [backdropReady, setBackdropReady] = useState(false);
  useEffect(() => {
    if (linkEmail) setEmail(linkEmail);
  }, [linkEmail]);
  const { startTimer, stopTimer, setPlayfabData, setIsAdmin, stopLoadingTimer } =
    useContext(purewebContext);

  useEffect(() => {
    localStorage.setItem("userEmail", email);
  }, [email]);

  useEffect(() => {
    const inputField = document.getElementById("login_email_field");
    const passwordField = document.getElementById("login_password_field");
    function handleEvent(event) {
      if (event.key === "Tab") { event.preventDefault(); passwordField?.focus?.(); }
    }
    function handleEvent2(event) {
      if (event.key === "Tab") { event.preventDefault(); inputField?.focus?.(); }
    }
    inputField?.addEventListener("keydown", handleEvent);
    passwordField?.addEventListener("keydown", handleEvent2);
    return () => {
      inputField?.removeEventListener("keydown", handleEvent);
      passwordField?.removeEventListener("keydown", handleEvent2);
    };
  }, [loginMode]);

  useEffect(() => {
    return () => {
      if (loginRetryInterval) clearInterval(loginRetryInterval);
    };
  }, [loginRetryInterval]);

  const fetchPlayfabPlayers = async ({ email, password, idToken, msToken }) => {
    try {
      const body = idToken
        ? { email, idToken }
        : msToken
        ? { email, msToken }
        : { email, password };
      const response = await axios.post(
        `${API_BASE_URL}/playerdetailsFrontend`,
        body
      );
      setPlayfabData(response.data);
      // A 200 here means the signed-in email is in the admin registry (even if the
      // returned stats array is empty), which is what gates the Share Viewer Link.
      setIsAdmin(true);
    } catch (e) {
      setPlayfabData([]);
      setIsAdmin(false);
    }
  };

  const stopLoginRetry = useCallback(() => {
    if (loginRetryInterval) {
      clearInterval(loginRetryInterval);
      setLoginRetryInterval(null);
    }
  }, [loginRetryInterval]);

  useEffect(() => {
    if (error) {
      stopLoginRetry();
      setLoginLoading(false);
    }
  }, [error, stopLoginRetry]);

  // A success reply (e.g. account created) is a terminal outcome — stop the safety-net
  // timer so a successful creation doesn't later fire the "taking longer than expected"
  // error after UE has already answered.
  useEffect(() => {
    if (success) stopLoginRetry();
  }, [success, stopLoginRetry]);

  // Always send BOTH fields on every login attempt, and always email-then-password
  // with a short gap between them. On the UE side, receiving `LoginPassword` is what
  // triggers the PlayFab login call, while `LoginEmail` just sets the email variable.
  // If both are sent in the same tick they can be processed out of order, so UE logs
  // in with a stale email from a previous attempt — the "stuck on Logging in..." bug
  // that appears after a user edits a field and retries. Sequencing email first,
  // then password after a small delay, guarantees the fresh email is in place before
  // the login fires. This same sequence is reused by the post-signup login, so a new
  // user signing in for the first time is not affected by the stale-state bug either.
  const sendLoginMessages = useCallback(() => {
    if (sendMessage && email && password) {
      sendMessage(`LoginEmail,${email}`);
      setTimeout(() => {
        sendMessage(`LoginPassword,${password}`);
        console.log("Login messages sent");
      }, 150);
    }
  }, [sendMessage, email, password]);

  // A login is NOT idempotent: every resend of the credentials is a fresh
  // PlayFab authentication attempt. The old 2s-interval resend fired again
  // before UE could answer, so a single wrong password produced several failed
  // attempts and eventually tripped PlayFab's lockout (and the 30-attempt
  // timeout in the UI). UE reliably replies with a success or error message,
  // so we send the credentials ONCE and let that reply drive the UI. The
  // timeout is only a safety net for the (rare) case where no reply arrives at
  // all — it never resends credentials.
  const startLoginRetry = useCallback(() => {
    stopLoginRetry();
    sendLoginMessages();

    const timeout = setTimeout(() => {
      stopLoginRetry();
      setError("Login is taking longer than expected. Please try again.");
      onLoginTimeout?.("login");
    }, 30000);

    setLoginRetryInterval(timeout);
  }, [stopLoginRetry, sendLoginMessages, setError, onLoginTimeout]);

  // "Create New Account" (?action=signup) sends `NewUser` straight to UE — fire-and-forget.
  // If the stream / UE login state isn't ready yet the message is silently dropped and no
  // reply ever comes back, leaving the button stuck on "Creating New Account…" forever.
  // That dead-end is what makes people refresh the page (and then lose their intake form).
  // Mirror the login safety net: send the request ONCE (a resend risks creating a duplicate
  // account) and, if no reply arrives in time, surface a retryable error instead of an
  // infinite spinner. UE's success/error reply clears newAccountLoading; success also clears
  // this timer (see the success effect below), so a slow-but-successful creation never trips
  // the timeout.
  const startNewAccountRetry = useCallback((accountEmail) => {
    stopLoginRetry();
    if (sendMessage && accountEmail) sendMessage(`NewUser,${accountEmail}`);

    const timeout = setTimeout(() => {
      stopLoginRetry();
      setNewAccountLoading(false);
      setError("Account creation is taking longer than expected. Please try again.");
      onLoginTimeout?.("new_account");
    }, 30000);

    setLoginRetryInterval(timeout);
  }, [stopLoginRetry, sendMessage, setNewAccountLoading, setError, onLoginTimeout]);

  const startMicrosoftLoginRetry = useCallback((token) => {
    stopLoginRetry();
    if (sendMessage && token) sendMessage(`Login_Token_Microsoft,${token}`);

    const timeout = setTimeout(() => {
      stopLoginRetry();
      setError("Login is taking longer than expected. Please try again.");
      onLoginTimeout?.("microsoft_login");
    }, 30000);

    setLoginRetryInterval(timeout);
  }, [stopLoginRetry, sendMessage, setError, onLoginTimeout]);

  // The browser performs NO Google OAuth. It only asks UE to start its own flow; UE
  // replies with a consent URL we open for the user (see onGoogleTrigger in the
  // emulator). Everything after that — polling, PlayFab, the account chooser — is UE's.
  const handleGoogleLogin = useCallback(() => {
    setError("");
    setLoginLoading(true);
    onGoogleTrigger?.();
  }, [onGoogleTrigger, setError]);

  const handleMicrosoftLogin = useCallback(async () => {
    setError("");
    setLoginLoading(true);
    try {
      const result = await msalInstance.loginPopup({ scopes: ["User.Read"] });
      const accessToken = result.accessToken;

      if (onDirectSocialLogin) {
        await onDirectSocialLogin("microsoft", accessToken);
        return;
      }
      // Started here rather than before the popup so the user's time in Microsoft's
      // consent screen doesn't eat the login timeout budget.
      startTimer();

      const response = await axios.post(
        `${API_BASE_URL}/validateMicrosoftToken`,
        { accessToken }
      );
      const { email } = response.data;
      localStorage.setItem("userEmail", email);
      startMicrosoftLoginRetry(accessToken);
      await fetchPlayfabPlayers({ email, msToken: accessToken });
    } catch (e) {
      if (e.errorCode !== "user_cancelled") {
        setError(e.response?.data?.message || "Microsoft sign-in failed. Please try again.");
      }
      stopTimer();
      stopLoginRetry();
      setLoginLoading(false);
    }
  }, [startTimer, stopTimer, stopLoginRetry, startMicrosoftLoginRetry, setError, onDirectSocialLogin]);


  const handleLogin = useCallback(async () => {
    setError("");
    setSuccess("");
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError("Email and password are required.");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (linkEmail && onGoogleLinkLogin) {
      setLoginLoading(true);
      try {
        await onGoogleLinkLogin(trimmedEmail, password);
      } catch (e) {
        setError("Could not verify your credentials. Please try again.");
        setLoginLoading(false);
      }
      return;
    }
    if (onDirectLogin) {
      // The parent validates against the backend, stores the credentials, and
      // auto-logs in once UE loads — it owns error/loading state from here.
      setLoginLoading(true);
      try {
        await onDirectLogin(trimmedEmail, password);
      } catch (e) {
        setError("Could not verify your credentials. Please try again.");
        setLoginLoading(false);
      }
      return;
    }
    startTimer();
    try {
      if (sendMessage) {
        setLoginLoading(true);
        if (!loginRetryInterval) startLoginRetry();
        await fetchPlayfabPlayers({ email, password });
      }
    } catch (e) {
      console.log("ERROR LOGGING IN", e);
      stopTimer();
      stopLoginRetry();
    }
  }, [sendMessage, email, password, startLoginRetry, stopLoginRetry, loginRetryInterval, onDirectLogin, linkEmail, onGoogleLinkLogin]);

  const handleResetPassword = useCallback(() => {
    setError("");
    setSuccess("");
    if (loginMode && !resetPasswordMode) {
      setLoginMode(false);
      setResetPasswordMode(true);
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please enter your email address.");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    setResetPasswordLoading(true);
    sendMessage(`ResetPassword,${email}`);
    console.log("Reset password message sent");
  }, [sendMessage, email, loginMode, resetPasswordMode]);

  const handleSignUp = useCallback(() => {
    setError("");
    setSuccess("");
    if (onSignupRedirect) {
      // Direct-login mode: new users must go through the signup intake flow
      // (the same one ?action=signup shows) instead of the UE NewUser message.
      onSignupRedirect();
      return;
    }
    if (loginMode && !newAccountMode) {
      setLoginMode(false);
      setNewAccountMode(true);
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please enter your email address.");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    setNewAccountLoading(true);
    // Send trimmed so the account is created under the same email login validates against.
    startNewAccountRetry(trimmedEmail);
    console.log("New account message sent");
  }, [startNewAccountRetry, email, loginMode, newAccountMode, onSignupRedirect]);

  const backToLogin = () => {
    setLoginLoading(false);
    setResetPasswordLoading(false);
    setNewAccountLoading(false);
    setLoginMode(true);
    setResetPasswordMode(false);
    setNewAccountMode(false);
    setEmail("");
    setPassword("");
    setSuccess("");
    setError("");
  };

  // After a new account is created, reveal the login fields with the email
  // pre-filled so the user can sign in immediately once they've set a password.
  const handleAccountCreatedContinue = () => {
    setNewAccountLoading(false);
    setLoginMode(true);
    setResetPasswordMode(false);
    setNewAccountMode(false);
    setPassword("");
    setSuccess("");
    setError("");
    onAccountCreatedContinue?.();
  };

  return (
    <div
      className={`login-container il-login-overlay${
        claritySeeThrough && backdropReady ? " il-backdrop-see-through" : ""
      }`}
    >
      {claritySeeThrough && (
        <ClaritySeeThroughBackdrop color="#2a2a2a" onReady={() => setBackdropReady(true)} />
      )}
      <div className="login-box">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }} className="login-box--logo">
          <div style={{ maxWidth: 250 }}>
            <img style={{ zIndex: 2 }} src={logoImage} className="logo logo-display widthofimage" alt="Logo" />
          </div>
        </div>
        <div className="login-box--title">
          {loginMode ? "User Login" : resetPasswordMode ? "Reset Password" : "New Account"}
        </div>
        <div className="login-box--subtitle">
          {loginMode
            ? linkEmail
              ? "We found an existing account for this email. Enter your password once to link it to your Google sign-in."
              : "Returning users login with your email and password. Otherwise, create a new account."
            : resetPasswordMode
            ? "Enter email below and we will send a reset password link."
            : "Enter email below to create a new account."}
        </div>
        <div style={{ display: "flex", flexDirection: "column", marginBottom: "20px", position: "relative" }}>
          <input
            id="login_email_field"
            placeholder="Enter your email"
            value={email}
            type="email"
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            name="email"
            readOnly={Boolean(linkEmail)}
          />
          {loginMode && (
            <div className="password-input-wrapper">
              <input
                id="login_password_field"
                placeholder="Enter your password"
                value={password}
                type={showPassword ? "text" : "password"}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                name="password"
              />
              <div className="password-toggle-icon" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={16} color="#6b7280" /> : <Eye size={18} color="#6b7280" />}
              </div>
            </div>
          )}
          {error && (
            <div style={{ color: "#e83232", textAlign: "left", marginTop: -15 }}>{error}</div>
          )}
          {success && !newAccountMode && (
            <div style={{ color: "#55d655", textAlign: "left", marginTop: -15 }}>{success}</div>
          )}
        </div>
        {loginMode && (
          <button onClick={handleLogin} style={{ backgroundColor: "#0AAC41" }} disabled={loginLoading}>
            {loginLoading ? "Logging in..." : "Login"}
          </button>
        )}
        {(loginMode || resetPasswordMode) && !hideResetPassword && (
          <button onClick={handleResetPassword} style={{ backgroundColor: "#959496" }} disabled={resetPasswordLoading}>
            {resetPasswordLoading ? "Sending Reset Password Email..." : "Reset Password"}
          </button>
        )}
        {(loginMode || (newAccountMode && !success)) && !linkEmail && (
          <button onClick={handleSignUp} style={{ backgroundColor: "#0069BA" }} disabled={newAccountLoading}>
            {newAccountLoading ? "Creating New Account..." : "Create New Account"}
          </button>
        )}
        {linkEmail && loginMode && (
          <button onClick={onLinkCancel} style={{ backgroundColor: "#959496" }} disabled={loginLoading}>
            Cancel
          </button>
        )}
        {(resetPasswordMode || newAccountMode) && (
          <button onClick={backToLogin} style={{ backgroundColor: "#0069BA" }}>
            Back to Login
          </button>
        )}
        {(loginMode || newAccountMode) && !linkEmail && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0" }}>
            <div style={{ flex: 1, height: 1, backgroundColor: "#444" }} />
            <span style={{ color: "#888", fontSize: 12 }}>or</span>
            <div style={{ flex: 1, height: 1, backgroundColor: "#444" }} />
          </div>
        )}
        {(loginMode || newAccountMode) && !linkEmail && (
          <button
            onClick={handleGoogleLogin}
            disabled={loginLoading || newAccountLoading}
            style={{ backgroundColor: "#fff", color: "#3c4043", border: "1px solid #dadce0", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908C16.658 13.074 17.64 11.229 17.64 9.2z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.26A5.36 5.36 0 0 1 9 14.42c-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.892 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>
        )}
        {!IS_PRODUCTION && (loginMode || newAccountMode) && !linkEmail && (
          <button
            onClick={handleMicrosoftLogin}
            disabled={loginLoading || newAccountLoading}
            style={{ backgroundColor: "#fff", color: "#3c4043", border: "1px solid #dadce0", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="10" height="10" fill="#F25022"/>
              <rect x="11" y="0" width="10" height="10" fill="#7FBA00"/>
              <rect x="0" y="11" width="10" height="10" fill="#00A4EF"/>
              <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
            </svg>
            Continue with Microsoft
          </button>
        )}
        {newAccountMode && success && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 10,
              backgroundColor: "rgba(18, 18, 18, 0.98)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: 40,
              zIndex: 5,
            }}
          >
            <div style={{ fontSize: 56, lineHeight: 1, marginBottom: 16 }}><AiOutlineCheckCircle color="green" size={60}/></div>
            <div style={{ fontSize: 28, fontWeight: 600, marginBottom: 16 }}>
              Check Your Email
            </div>
            <div style={{ fontSize: 17, lineHeight: 1.5, color: "#d0d0d0", maxWidth: 420 }}>
              Your account was created! Check your inbox for an email from <strong style={{ color: "#fff" }}>Playfab</strong> with
              the subject <strong style={{ color: "#fff" }}>"RenderNow account recovery"</strong>. Open it
              to set your password, then come back here and click OK to log in.
            </div>
            <button
              onClick={handleAccountCreatedContinue}
              style={{ backgroundColor: "#0069BA", marginTop: 28, minWidth: 200 }}
            >
              OK
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
